/**
 * Source of truth for /vs/ comparison pages.
 *
 * Each competitor below is rendered into a structured comparison
 * page with consistent claims, citations, and CTA. Edit here, not
 * in the page component, so the marketing voice stays consistent.
 *
 * Pricing claims that come from third-party trackers / forum threads
 * are explicitly cited. "Contact sales" prices are flagged for the
 * reader so they can verify directly.
 */

export type CompetitorVertical =
  | "self_storage"
  | "multifamily"
  | "vehicle_storage"
  | "parking";

export interface Competitor {
  slug: string;
  display_name: string;
  /** Which vertical this competitor primarily serves. Drives the
   * /vs/{slug} index grouping + cross-link to /verticals/{vertical}. */
  vertical: CompetitorVertical;
  /** Short tagline rendered under the h1. */
  one_liner: string;
  /** What the competitor says they cost per facility per month. */
  competitor_pricing: {
    headline: string; // e.g. "$230–$1,000/facility/month"
    transparent: boolean; // do they publish this?
    citation: string; // URL or "Self-Storage Talk: ..."
    citation_label: string; // human-readable display
  };
  /** The marketing-voice opener for this comparison. */
  opener: string;
  /** Bullet rows for the comparison table. */
  rows: Array<{
    label: string;
    nestplot: string;
    competitor: string;
    nestplot_better: boolean;
  }>;
  /** Specific operator complaints worth surfacing. */
  complaints: string[];
  /** SEO meta. */
  meta_title: string;
  meta_description: string;
}

/** Vertical-specific reusable strings — referenced from per-vertical
 * comparison rows the subagent generates. Multifamily / RV-boat /
 * parking pick from the relevant block. */
export const NESTPLOT_MAINTENANCE =
  "In-product maintenance Kanban + tenant-portal ticket submission";
export const NESTPLOT_APPLICATIONS =
  "Online applications + screening-ready API + leasing pipeline Kanban";
export const NESTPLOT_DOCK_PLAN =
  "Dock-plan editor with LOA bracket pricing + hookup callouts";
export const NESTPLOT_BOAT_ASSET =
  "Per-vessel record (HIN, LOA, beam, draft, insurance carrier)";
export const NESTPLOT_PARKING_PERMIT =
  "Per-permit-holder records + gate-code-on-pay + permit expiry tracking";
export const NESTPLOT_TRANSIENT =
  "Roadmap: transient parking (daily/hourly) — monthly today";

const NESTPLOT_PRICE = "From $59/mo flat (published)";
const NESTPLOT_STRIPE = "Bring your own Stripe Connect · 0% take";
const NESTPLOT_MINIMUM = "No minimum facilities or units";
const NESTPLOT_LEASE = "All-50-states + DC lease engine with court-ready audit trail";
// Updated 2026-06-23 — F6 added Noke/DaVinciLock/IntelliGate/Janus
// to the existing PTI/SecurGuard/INSOMNIAC trio.
const NESTPLOT_GATE = "7 vendors: PTI, SecurGuard, INSOMNIAC, Noke, DaVinciLock, IntelliGate, Janus";
const NESTPLOT_LIEN =
  "In-product lien-sale workflow + Lob.com certified mail (USPS tracking)";
const NESTPLOT_IDENTITY = "Stripe Identity gate · operator-portfolio-wide pass-through";
const NESTPLOT_ACCOUNTING = "QuickBooks + Xero + Sage Intacct, multi-entity";
const NESTPLOT_RBAC = "Fine-grained role permissions + a tamper-evident audit trail";
const NESTPLOT_PORTFOLIO = "Cross-facility portfolio dashboard with per-team-member access controls";

export const COMPETITORS: Competitor[] = [
  {
    slug: "storedge",
    display_name: "storEDGE",
    vertical: "self_storage",
    one_liner: "Storable's flagship — full-featured, enterprise pricing, post-acquisition support pain.",
    competitor_pricing: {
      headline: "$230–$1,000/facility/month",
      transparent: false,
      citation: "https://softwareconnect.com/roundups/best-self-storage-software/",
      citation_label: "SoftwareConnect roundup",
    },
    opener:
      "Same operator workflow at one-fifth the price, support that answers in hours not weeks, and tenant payments don't fund Storable's revenue.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "Contact sales", nestplot_better: true },
      { label: "Setup fee", nestplot: "$0", competitor: "$1,000–$5,000 reported", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Storable Payments (processing margin)", nestplot_better: true },
      { label: "Minimum facilities", nestplot: NESTPLOT_MINIMUM, competitor: "1+ but priced for multi-facility", nestplot_better: true },
      { label: "State-specific lease engine", nestplot: NESTPLOT_LEASE, competitor: "Yes (20+ states)", nestplot_better: false },
      { label: "Gate integration", nestplot: NESTPLOT_GATE, competitor: "Storable-only ecosystem", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Notice PDF only — no certified mail", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "QuickBooks only", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Team permissions + audit", nestplot: NESTPLOT_RBAC, competitor: "Coarse user roles", nestplot_better: true },
      { label: "Cross-facility portfolio", nestplot: NESTPLOT_PORTFOLIO, competitor: "Per-facility navigation", nestplot_better: true },
      { label: "Modern stack / shipping cadence", nestplot: "Weekly releases", competitor: "Months-long backlog", nestplot_better: true },
    ],
    complaints: [
      "Support tickets disappear; week+ response times post-Storable acquisition",
      "Known bugs sit in engineering backlog with no ETA",
      "Regular price hikes since Storable bought storEDGE",
      "Storable Payments is non-optional — operator can't bring own processor",
    ],
    meta_title: "NestPlot vs storEDGE — flat from $59/mo alternative",
    meta_description:
      "NestPlot is the operator-owned alternative to storEDGE: flat from $59/mo, bring your own Stripe, no setup fee, all-50-state lease engine built in.",
  },
  {
    slug: "sitelink",
    display_name: "SiteLink",
    vertical: "self_storage",
    one_liner: "The legacy self-storage software with the largest install base — modernized through acquisition, not architecture.",
    competitor_pricing: {
      headline: "$150–$1,200/facility/month",
      transparent: false,
      citation: "Self-Storage Talk operator threads",
      citation_label: "Self-Storage Talk · forum disclosure",
    },
    opener:
      "Same legal-compliance depth, on a modern stack — and we publish what it costs.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "Contact sales", nestplot_better: true },
      { label: "Tech stack", nestplot: "FastAPI + React, SSR ready", competitor: "Windows Web Edition (legacy)", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "SiteLink Merchant Services", nestplot_better: true },
      { label: "Setup fee", nestplot: "$0", competitor: "Typically $500–$1,500", nestplot_better: true },
      { label: "Tenant portal", nestplot: "Modern mobile-first", competitor: "Functional, dated UI", nestplot_better: true },
      { label: "Gate integration", nestplot: NESTPLOT_GATE, competitor: "PTI + SecurGuard via partners", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Letter merge — no in-product certified mail", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "QuickBooks export", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Team permissions + audit", nestplot: NESTPLOT_RBAC, competitor: "Role-based; audit is per-action only", nestplot_better: true },
      { label: "Open API", nestplot: "First-party REST API + Stripe webhooks", competitor: "Closed; integrations via partner-only ecosystem", nestplot_better: true },
    ],
    complaints: [
      "Tenant-facing UI feels dated next to modern alternatives",
      "Per-user licensing model adds cost as facility grows",
      "Reporting requires Crystal Reports knowledge for custom views",
    ],
    meta_title: "NestPlot vs SiteLink — modern alternative for storage operators of every size",
    meta_description:
      "SiteLink dominates the install base but charges per-user on a Windows-era stack. NestPlot is flat from $59/mo, modern, mobile-first, with Stripe Connect built in.",
  },
  {
    slug: "easy-storage-solutions",
    display_name: "Easy Storage Solutions",
    vertical: "self_storage",
    one_liner: "Storable-owned (since 2020). $90/mo entry point, but locks operators into a single payment processor.",
    competitor_pricing: {
      headline: "$90/month",
      transparent: true,
      citation: "https://www.capterra.com/p/112769/Self-Storage-Software/pricing",
      citation_label: "Capterra pricing tracker",
    },
    opener:
      "Bring your own Stripe — keep your processing margin.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "$90/month", nestplot_better: false },
      { label: "Payment processing choice", nestplot: "Any Stripe Connect account", competitor: "ESS-mandated merchant only", nestplot_better: true },
      { label: "Audit trail on tenant edits", nestplot: "Hash-chained lease audit", competitor: "Notes can be permanently deleted", nestplot_better: true },
      { label: "Support hours", nestplot: "Email + in-app, 7d response SLA", competitor: "M–F 8–5 MST only", nestplot_better: true },
      { label: "Gate integration", nestplot: NESTPLOT_GATE, competitor: "Limited brand support", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Notice PDF — operator mails it", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "QuickBooks export only", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Team permissions + audit", nestplot: NESTPLOT_RBAC, competitor: "Basic user roles", nestplot_better: true },
      { label: "Owner since", nestplot: "Independent", competitor: "Storable (since 2020)", nestplot_better: true },
    ],
    complaints: [
      "Can't pick own credit-card processor — locked into ESS-mandated rates",
      "Notes can be permanently deleted by anyone, with no audit log",
      "Tech support: M-F 8-5 MST only, no weekend coverage",
      "Gate hardware integration breaks for weeks with no replacement",
      "Post-Storable acquisition: support quality degraded",
    ],
    meta_title: "NestPlot vs Easy Storage Solutions — bring your own Stripe",
    meta_description:
      "Easy Storage Solutions is $90/mo but forces their merchant processor. NestPlot is flat from $59/mo with operator-owned Stripe Connect — keep your processing margin.",
  },
  {
    slug: "stora",
    display_name: "Stora",
    vertical: "self_storage",
    one_liner: "UK-built modern stack, expanding US presence. Strong website builder; 50-unit minimum; UK lease law first.",
    competitor_pricing: {
      headline: "$83–$249/month (Essentials / Advanced / Premium)",
      transparent: true,
      citation: "https://stora.co/pricing",
      citation_label: "Stora pricing page",
    },
    opener:
      "You don't need 50 units to start, and we cover CA / FL / TX / NY / MA state law out of the box — Stora was built for UK leases.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "$83–$249/mo", nestplot_better: false },
      { label: "Minimum unit count", nestplot: "None", competitor: "50 units (hard gate)", nestplot_better: true },
      { label: "State-specific lease engine", nestplot: NESTPLOT_LEASE, competitor: "UK-first; US states uneven", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Stora-mediated processing", nestplot_better: true },
      { label: "Support channel", nestplot: "Email + in-app, ≤1h response", competitor: "Phone — Premium tier only", nestplot_better: true },
      { label: "Website builder", nestplot: "Public rental page included", competitor: "Industry-leading builder", nestplot_better: false },
      { label: "Gate integration", nestplot: NESTPLOT_GATE, competitor: "Noke + Salto (UK-leaning)", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "UK process — US state coverage uneven", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "Xero (UK-first)", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
    ],
    complaints: [
      "Reporting is shallow vs SiteLink/storEDGE",
      "Deals/CRM section unusable on mobile",
      "Support pushes chat — no phone unless Premium",
      "UK-first means US state law coverage is uneven",
    ],
    meta_title: "NestPlot vs Stora — flat from $59/mo, no unit minimum, US state law",
    meta_description:
      "Stora's modern UX is great, but gates at 50 units and is UK-built. NestPlot is flat from $59/mo with all-50-state US lease engine and no minimum.",
  },
  {
    slug: "uhaul",
    display_name: "U-Haul WebSelfStorage",
    vertical: "self_storage",
    one_liner: "Free-ish ($44.95/mo, $34.95 for U-Haul dealers) — but U-Haul owns your customer relationship.",
    competitor_pricing: {
      headline: "$44.95/month ($34.95 for U-Haul dealers)",
      transparent: true,
      citation: "https://webselfstorage.com/PartnerWithUs",
      citation_label: "WebSelfStorage partner page",
    },
    opener:
      "Keep your tenants yours — NestPlot isn't U-Haul's lead-gen funnel, and your gate codes won't randomly lock paying customers out.",
    rows: [
      { label: "Tenant ownership", nestplot: "Your tenants, your data", competitor: "U-Haul routes reservations between affiliates", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "U-Haul processor only", nestplot_better: true },
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "$44.95/mo", nestplot_better: false },
      { label: "Gate reliability", nestplot: NESTPLOT_GATE, competitor: "Tenants reportedly denied while paid", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Per-state forms; no certified mail", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "U-Haul-mediated reporting", nestplot_better: true },
      { label: "U-Haul truck commission integration", nestplot: "No (you can DIY)", competitor: "21% U-Haul rental commission", nestplot_better: false },
    ],
    complaints: [
      "Tenants randomly denied at gate while paid",
      "Moved-out tenants' codes stay active (gate liability)",
      "Double-charging on autopay",
      "U-Haul has visibility into your tenants + pricing",
    ],
    meta_title: "NestPlot vs U-Haul WebSelfStorage — own your tenants",
    meta_description:
      "WebSelfStorage is cheap but U-Haul owns your customer relationship and processor. NestPlot is flat from $59/mo with operator-owned Stripe and stable gate integrations.",
  },
  {
    slug: "cubby",
    display_name: "Cubby",
    vertical: "self_storage",
    one_liner: "Just raised $63M from Goldman Sachs. Sales-gated pricing. Going upmarket to 400+ multi-facility operators.",
    competitor_pricing: {
      headline: "Not published — contact sales",
      transparent: false,
      citation:
        "https://fortune.com/2026/01/22/exclusive-cubby-raises-63-million-goldman-sachs-led-funding-scale-self-storage-software/",
      citation_label: "Fortune · Cubby Series A coverage",
    },
    opener:
      "Same modern stack, transparent pricing from $59, no sales call — Cubby won't even tell you what it costs.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "Demo only — won't disclose", nestplot_better: true },
      { label: "ICP", nestplot: "Operators of every size · self-serve onboarding", competitor: "400+ operators, 1,125 avg units", nestplot_better: true },
      { label: "Self-serve signup", nestplot: "Sign up + go in 60s", competitor: "Sales call mandatory", nestplot_better: true },
      { label: "Dynamic pricing", nestplot: "Auto-apply with operator-set caps + audit trail", competitor: "ML-native", nestplot_better: false },
      { label: "Gate integration", nestplot: NESTPLOT_GATE, competitor: "Noke + select Salto only", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Sales call to confirm; not in demo", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "Not disclosed publicly", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not disclosed publicly", nestplot_better: true },
      { label: "Team permissions + audit", nestplot: NESTPLOT_RBAC, competitor: "Sales call to confirm", nestplot_better: true },
    ],
    complaints: [
      "Demo gate frustrates anyone evaluating multiple vendors",
      "Pricing-for-VC-returns means likely 2-4x NestPlot cost",
      "Sales-led motion is poor fit for solo operators",
    ],
    meta_title: "NestPlot vs Cubby — transparent pricing for operators of every size",
    meta_description:
      "Cubby is Goldman-backed and going upmarket. NestPlot is the solo-operator alternative: flat from $59/mo, sign up online, no sales call.",
  },
  {
    slug: "hummingbird",
    display_name: "Hummingbird (Tenant Inc)",
    vertical: "self_storage",
    one_liner: "Modern PMS from Tenant Inc. ~$274/mo + $1k onboarding (forum-disclosed). Pushes proprietary payment processor.",
    competitor_pricing: {
      headline: "~$274/month + $1,000 setup",
      transparent: false,
      citation:
        "https://www.selfstoragetalk.com/forum/general-self-storage-forums/day-to-day-management/221556-anyone-using-tenant-inc-hummingbird-management-software",
      citation_label: "Self-Storage Talk · operator disclosure",
    },
    opener:
      "Published price, no setup fee, and tenant rent runs through your own Stripe Connect — no Tenant Payments middleman.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "~$274/mo (forum-leaked)", nestplot_better: true },
      { label: "Setup fee", nestplot: "$0", competitor: "$1,000", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Tenant Payments (proprietary)", nestplot_better: true },
      { label: "SuperLease automation", nestplot: "AI lease template engine", competitor: "Yes (SuperLease)", nestplot_better: false },
      { label: "2-way tenant SMS", nestplot: "Yes (Twilio)", competitor: "Yes", nestplot_better: false },
      { label: "Gate integration", nestplot: NESTPLOT_GATE, competitor: "Noke (Tenant Inc owns Noke parent)", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Yes, no certified-mail integration", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "QuickBooks integration", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Onboarding speed", nestplot: "Self-serve, ~60s", competitor: "Demo + setup call", nestplot_better: true },
    ],
    complaints: [
      "Bugs with multi-day support resolution",
      "Missing retail/merchandise return feature",
      "On-call setup fee is a surprise to many operators",
      "Sales-call gate frustrates self-serve buyers",
    ],
    meta_title: "NestPlot vs Hummingbird — no setup fee, no proprietary processor",
    meta_description:
      "Hummingbird is $274/mo + $1k setup with a proprietary payment processor. NestPlot is flat from $59/mo with Stripe Connect — and no setup fee.",
  },
  {
    slug: "doorloop",
    display_name: "DoorLoop",
    vertical: "self_storage",
    one_liner: "Residential property management tool with a self-storage checkbox. Per-unit pricing punishes small operators.",
    competitor_pricing: {
      headline: "$69/mo base + $1.00–$1.50/unit over 20",
      transparent: true,
      citation: "https://www.doorloop.com/pricing",
      citation_label: "DoorLoop pricing page",
    },
    opener:
      "DoorLoop is a residential landlord tool with a storage checkbox — NestPlot has the gate API, state lien workflow, and storage lease templates built in for less money.",
    rows: [
      { label: "Self-storage specialization", nestplot: "Built only for storage", competitor: "Residential PM with storage track", nestplot_better: true },
      { label: "200-unit facility cost", nestplot: "$99/mo flat (Growth)", competitor: "$69 + 180 overage = $249/mo", nestplot_better: true },
      { label: "Native gate integration", nestplot: NESTPLOT_GATE, competitor: "Not native", nestplot_better: true },
      { label: "State-specific lien workflow", nestplot: NESTPLOT_LIEN, competitor: "Generic; no certified mail", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "QuickBooks + Xero (no Sage)", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Tenant announcements", nestplot: "Included", competitor: "Higher tier only", nestplot_better: true },
    ],
    complaints: [
      "Hidden fees beyond base subscription",
      "Support quality 'virtually nonexistent' (Trustpilot)",
      "Tenant announcements + owner portal locked behind higher tiers",
      "DoorLoop Payments slow/confusing",
      "Inexperienced support staff",
    ],
    meta_title: "NestPlot vs DoorLoop — storage-native, flat price",
    meta_description:
      "DoorLoop is a residential PM tool with a storage track. NestPlot is built only for storage: state lien workflow, native gate integration, flat from $59/mo.",
  },
  // ====================================================================
  // MULTIFAMILY
  // ====================================================================
  {
    slug: "appfolio",
    vertical: "multifamily",
    display_name: "AppFolio Property Manager",
    one_liner:
      "Enterprise multifamily PMS with per-unit pricing, a 50-unit effective minimum, and a 2023 eCheck fee that turned 'free ACH' into ~$1/unit/month.",
    competitor_pricing: {
      headline: "~$298/mo minimum + $1.49/unit Core (Plus ~$960, Max ~$7,500)",
      transparent: false,
      citation: "https://checkthat.ai/brands/appfolio/pricing",
      citation_label: "CheckThat.ai pricing breakdown (vendor page is contact-sales)",
    },
    opener:
      "AppFolio is the default for management companies that already have 500 units. If you're under that, you'll pay the minimum, eat the eCheck fee, and wait on hold — NestPlot is flat from $59/mo with Stripe Connect and no per-unit overage.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "Contact sales (~$298/mo min + per-unit)", nestplot_better: true },
      { label: "Effective per-unit cost @ 50 units", nestplot: "$1.18/unit ($59 / 50)", competitor: "~$5.60/unit (min + per-unit)", nestplot_better: true },
      { label: "eCheck / ACH fee", nestplot: "Stripe pass-through, operator controls", competitor: "$2.49 per eCheck (since July 2023)", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "AppFolio Payments (mandatory)", nestplot_better: true },
      { label: "Minimum unit count", nestplot: NESTPLOT_MINIMUM, competitor: "Effective ~50-unit floor via $298 min", nestplot_better: true },
      { label: "Lease engine", nestplot: NESTPLOT_LEASE, competitor: "State lease packs (Plus tier)", nestplot_better: true },
      { label: "Maintenance + work orders", nestplot: NESTPLOT_MAINTENANCE, competitor: "Included (strong)", nestplot_better: false },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Available (Plus tier)", nestplot_better: false },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "Native GL (strong)", nestplot_better: false },
      { label: "Team permissions + audit", nestplot: NESTPLOT_RBAC, competitor: "Role-based; audit gaps reported", nestplot_better: true },
      { label: "Support SLA", nestplot: "Email + in-app, ≤1d response", competitor: "G2: 'on hold over 2 hours,' AI chat first", nestplot_better: true },
      { label: "Self-serve signup", nestplot: "Sign up + go in 60s", competitor: "Sales-led demo + onboarding", nestplot_better: true },
    ],
    complaints: [
      "July 2023 mandatory $2.49 eCheck fee — operators eat ~$1/unit/mo on vacants or pass to residents",
      "G2 / BiggerPockets: 'on hold over 2 hours,' tickets 'closed due to inactivity,' canned AI-chat replies",
      "Per-unit minimum punishes small operators — at 50 units the effective rate is ~4x the advertised $1.49",
      "Punitive billing: kept billing de-listed properties, threatened to disable Payments on chargeback dispute (BiggerPockets)",
      "Advanced workflows require a >3x jump from Core (~$298) to Plus (~$960) tier",
    ],
    meta_title: "NestPlot vs AppFolio — flat from $59/mo for multifamily operators of every size",
    meta_description:
      "AppFolio is built for 500+ unit management companies — per-unit pricing, $2.49 eCheck fees, sales-led onboarding. NestPlot is flat from $59/mo with Stripe Connect.",
  },
  {
    slug: "buildium",
    vertical: "multifamily",
    display_name: "Buildium",
    one_liner:
      "RealPage-owned mid-market PMS with published base tiers but a long tail of per-action fees — and a fresh DOJ antitrust monitor on the parent.",
    competitor_pricing: {
      headline: "$62 Essential / $192 Growth / $400 Premium per month",
      transparent: true,
      citation: "https://www.buildium.com/pricing/",
      citation_label: "Buildium pricing page",
    },
    opener:
      "The Essential tier looks cheap until you hit the $99 bank-account setup, $5/document eSignatures, and 'API only on Premium' wall. NestPlot is flat from $59/mo with the integrations and audit trail included.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "$62 / $192 / $400 per month", nestplot_better: false },
      { label: "Bank account setup", nestplot: "$0", competitor: "$99 per account", nestplot_better: true },
      { label: "eSignatures", nestplot: "Included", competitor: "$5/doc Essential, $1/doc Growth+", nestplot_better: true },
      { label: "Incoming EFT fee", nestplot: NESTPLOT_STRIPE, competitor: "$2.35 per incoming EFT", nestplot_better: true },
      { label: "1099 e-filing", nestplot: NESTPLOT_ACCOUNTING, competitor: "$50 + $4.50/form", nestplot_better: true },
      { label: "API access", nestplot: "First-party REST API (included)", competitor: "Premium tier ($400/mo) only", nestplot_better: true },
      { label: "Live phone support", nestplot: "Email + in-app, ≤1d", competitor: "Growth+ tier only", nestplot_better: true },
      { label: "Lease engine", nestplot: NESTPLOT_LEASE, competitor: "Template library, no court-ready audit trail", nestplot_better: true },
      { label: "Maintenance + work orders", nestplot: NESTPLOT_MAINTENANCE, competitor: "Included", nestplot_better: false },
      { label: "Owner / investor portal", nestplot: "Included", competitor: "Growth+ tier only", nestplot_better: true },
      { label: "Parent company", nestplot: "Independent", competitor: "RealPage (DOJ antitrust monitor through 2028)", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Tenant screening add-on (per-report fee)", nestplot_better: true },
    ],
    complaints: [
      "Mid-contract price hikes post-RealPage: Premium 150 jumped $337.50 → $400 on existing customers",
      "Surprise integration removals — Vendoroo maintenance-coordinator cut 10/22/2025; API restoration requires tier upgrade",
      "Hidden fees: $99 bank setup, $5/doc eSignatures, $2.35 EFT, $50 + $4.50/form 1099 e-filing",
      "Essential tier is feature-starved — no live phone support, no API — forces $192 Growth upgrade in practice",
      "DOJ antitrust settlement (Nov 2025) brings 3-year monitor + algo-pricing restrictions to the RealPage portfolio",
    ],
    meta_title: "NestPlot vs Buildium — flat pricing without the per-action fees",
    meta_description:
      "Buildium's $62 Essential tier hides $99 bank setup, $5 eSignatures, and an API paywall. NestPlot is flat from $59/mo with API, audit trail, and Stripe Connect included.",
  },
  {
    slug: "doorloop-multifamily",
    vertical: "multifamily",
    display_name: "DoorLoop (multifamily)",
    one_liner:
      "Modern landlord-PM with a transparent $69 starter — capped at 10 units, with a separate merchant-account fee that doesn't show up until you ask.",
    competitor_pricing: {
      headline: "$69/mo Starter (annual) / $99 monthly — 10-unit cap; Pro $149+ for API + integrations",
      transparent: true,
      citation: "https://www.doorloop.com/pricing",
      citation_label: "DoorLoop pricing page",
    },
    opener:
      "DoorLoop's marketing leads with $69. The real cost is the Pro upgrade you need for API and Outlook plus a separate merchant-account fee on the rent-collection feature you bought it for. NestPlot is flat from $59/mo with API and Stripe Connect on day one.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "$69 annual / $99 monthly Starter (10-unit cap)", nestplot_better: false },
      { label: "Unit cap on entry tier", nestplot: NESTPLOT_MINIMUM, competitor: "10 units on Starter; upgrade required after", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "DoorLoop Payments — separate merchant fee not surfaced pre-sale", nestplot_better: true },
      { label: "API access", nestplot: "First-party REST API (included)", competitor: "Pro tier ($149+) only", nestplot_better: true },
      { label: "Gmail / Outlook sync", nestplot: "Included", competitor: "Pro tier only", nestplot_better: true },
      { label: "User roles + permissions", nestplot: NESTPLOT_RBAC, competitor: "Pro tier only", nestplot_better: true },
      { label: "Bank reconciliation", nestplot: NESTPLOT_ACCOUNTING, competitor: "Pro tier only", nestplot_better: true },
      { label: "Lease engine", nestplot: NESTPLOT_LEASE, competitor: "Template library, no certified-mail audit trail", nestplot_better: true },
      { label: "Maintenance + work orders", nestplot: NESTPLOT_MAINTENANCE, competitor: "Included", nestplot_better: false },
      { label: "Support hours", nestplot: "Email + in-app, ≤1d response", competitor: "Closed after 5pm ET + weekends", nestplot_better: true },
      { label: "Sales pressure", nestplot: "Self-serve signup, no call required", competitor: "Annual-contract urgency on demo calls", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Screening add-on, per-report fee", nestplot_better: true },
    ],
    complaints: [
      "Reps create 'sense of urgency to sign annual'; refund / downgrade requests bounced between chat and support",
      "Separate merchant-account fee for rent collection — not disclosed until after purchase (Capterra)",
      "Trustpilot: text-messaging charges only surfaced after 3 sales calls",
      "API, Gmail/Outlook, user roles, bank reconciliation all locked above Starter",
      "Slow ACH and 'unable to add bank account after months' reports",
    ],
    meta_title: "NestPlot vs DoorLoop — multifamily flat pricing with API included",
    meta_description:
      "DoorLoop's $69 Starter caps at 10 units and gates API + payments behind Pro. NestPlot is flat from $59/mo with API, Stripe Connect, and no per-unit overage.",
  },
  {
    slug: "rent-manager",
    vertical: "multifamily",
    display_name: "Rent Manager",
    one_liner:
      "LCS Software's deep, customizable mixed-portfolio PMS — but every module is unbundled, every quote is bespoke, and the UI hasn't moved in a decade.",
    competitor_pricing: {
      headline: "Contact sales (third-party estimates: ~$1.00–$2.25/unit/mo by tier + module add-ons)",
      transparent: false,
      citation: "https://www.rentmanager.com/pricing/",
      citation_label: "Rent Manager pricing page (Get Quote only)",
    },
    opener:
      "Rent Manager is the swiss army knife — and you pay for every blade separately. NestPlot is flat from $59/mo with the modules baked in and a modern stack you don't have to schedule three calls to evaluate.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "Contact sales (multi-call quote process)", nestplot_better: true },
      { label: "Module bundling", nestplot: "All features at one price", competitor: "Per-module add-ons (texting, signing, portal, etc.)", nestplot_better: true },
      { label: "UI / UX", nestplot: "Modern React, mobile-first", competitor: "Dated; 'tedious to navigate' (G2)", nestplot_better: true },
      { label: "Implementation time", nestplot: "Self-serve, ~60s", competitor: "Multi-week onboarding + training", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "rmResident — $5 ACH, $10 debit transfer fees reported", nestplot_better: true },
      { label: "Resident-side payment fees", nestplot: "Stripe pass-through, operator controls", competitor: "High convenience fees flagged on reviews", nestplot_better: true },
      { label: "Lease engine", nestplot: NESTPLOT_LEASE, competitor: "Template library, manual state handling", nestplot_better: true },
      { label: "Maintenance + work orders", nestplot: NESTPLOT_MAINTENANCE, competitor: "Included (strong, customizable)", nestplot_better: false },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "Native GL (strong)", nestplot_better: false },
      { label: "API / integrations", nestplot: "First-party REST API + Stripe webhooks", competitor: "rmAPI add-on, partner ecosystem", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Screening partner pass-through", nestplot_better: true },
      { label: "Support quality", nestplot: "Email + in-app, ≤1d response", competitor: "G2: 'severely lacking,' 'bugs not fixable'", nestplot_better: true },
    ],
    complaints: [
      "Outdated UI — 'dated and unintuitive,' 'tedious to navigate' (G2)",
      "Steep learning curve — multi-week setup is the recurring evaluation killer",
      "Every add-on costs — 'NO Free add-ons,' surprise expenses post-go-live",
      "Support: 'severely lacking,' specific bugs marked 'not fixable'",
      "rmResident: 'processing fees are incredibly high — $5 to transfer, $10 for debit'",
    ],
    meta_title: "NestPlot vs Rent Manager — modern flat-price alternative",
    meta_description:
      "Rent Manager is deep but every module costs extra and quotes are sales-gated. NestPlot is flat from $59/mo with API, payments, and audit trail built in.",
  },
  {
    slug: "yardi-breeze",
    vertical: "multifamily",
    display_name: "Yardi Breeze",
    one_liner:
      "Yardi's SMB on-ramp — built to funnel growing operators into Breeze Premier ($400/mo min) and eventually Voyager.",
    competitor_pricing: {
      headline: "$1/unit/mo Residential ($100/mo min); Breeze Premier $1/unit ($400/mo min)",
      transparent: true,
      citation: "https://www.yardibreeze.com/pricing/",
      citation_label: "Yardi Breeze pricing page",
    },
    opener:
      "Breeze is built to push you to Premier ($400/mo) the moment you need real automation. NestPlot is flat from $59/mo — the automation and API ship at the entry tier, not the upsell tier.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "$100/mo min (Residential) / $400/mo min (Premier)", nestplot_better: false },
      { label: "Effective per-unit @ 100 units", nestplot: "$0.99/unit ($99 / 100)", competitor: "$1/unit + $400 min upsell pressure", nestplot_better: true },
      { label: "Automated AP", nestplot: "Included", competitor: "Premier ($400/mo) only", nestplot_better: true },
      { label: "Customizable financials", nestplot: NESTPLOT_ACCOUNTING, competitor: "Premier only", nestplot_better: true },
      { label: "State-specific lease docs", nestplot: NESTPLOT_LEASE, competitor: "Premier only", nestplot_better: true },
      { label: "Owner reports", nestplot: "Included", competitor: "Premier only", nestplot_better: true },
      { label: "API access", nestplot: "First-party REST API (included)", competitor: "No public API on Breeze tier", nestplot_better: true },
      { label: "Maintenance + work orders", nestplot: NESTPLOT_MAINTENANCE, competitor: "Included (RentCafe-tied)", nestplot_better: false },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Yardi Payment Processing (mandatory)", nestplot_better: true },
      { label: "Performance", nestplot: "FastAPI + edge CDN", competitor: "G2: 'extremely slow, almost daily'", nestplot_better: true },
      { label: "Support quality", nestplot: "Email + in-app, ≤1d response", competitor: "G2: 'offline for days,' staff lack product knowledge", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Screening add-on", nestplot_better: true },
    ],
    complaints: [
      "G2: 'Almost daily, for at least part of the day, Yardi is extremely slow'",
      "Support taken 'offline' for days at a time; staff often lack product knowledge",
      "Premier-locked features: AP automation, owner reports, state lease docs, AP approval limits",
      "Buggy online applications + lease signing; lack of API forces manual workflows",
      "Designed as a funnel — Breeze → Premier → Voyager upsell pressure on growth",
    ],
    meta_title: "NestPlot vs Yardi Breeze — flat pricing without the Premier upsell",
    meta_description:
      "Breeze pushes automation behind the $400 Premier minimum. NestPlot is flat from $59/mo with AP, state leases, API, and Stripe Connect at the entry tier.",
  },
  {
    slug: "realpage-onesite",
    vertical: "multifamily",
    display_name: "RealPage OneSite",
    one_liner:
      "Enterprise multifamily suite for Top-50 NMHC operators — opaque pricing, dated UI, and an active DOJ antitrust monitor through 2028.",
    competitor_pricing: {
      headline: "Contact sales (third-party estimates: ~$400/mo floor + ~$100/mo per module)",
      transparent: false,
      citation: "https://www.itqlick.com/onesite-property-management-software/pricing",
      citation_label: "ITQlick hidden-cost estimate (vendor page is contact-sales)",
    },
    opener:
      "OneSite is built for 60,000-unit operators with six-figure implementation budgets. Whatever your portfolio size, NestPlot ships the operator workflow flat — without the DOJ headlines, the per-module nickel-and-diming, or the 'New Experience' rewrite that's been rolling out for two years.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "Contact sales (six-figure implementations typical)", nestplot_better: true },
      { label: "Target operator size", nestplot: "Operators of every size, self-serve", competitor: "Top-50 NMHC; 60,000+ unit portfolios", nestplot_better: false },
      { label: "Per-module add-on fees", nestplot: "Flat, all-modules included", competitor: "~$100/mo per acquired-product module", nestplot_better: true },
      { label: "Data export", nestplot: "Free CSV + API export", competitor: "Reviewers: 'charges a fee to download your own data'", nestplot_better: true },
      { label: "UI / UX", nestplot: "Modern React, mobile-first", competitor: "G2: 'completely outdated, not user-friendly'", nestplot_better: true },
      { label: "Antitrust posture", nestplot: "No regulatory monitor", competitor: "DOJ antitrust monitor through 2028 + $200M+ class-action settlements", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "RealPage Payments (mandatory in many modules)", nestplot_better: true },
      { label: "Lease engine", nestplot: NESTPLOT_LEASE, competitor: "State packs across acquired modules", nestplot_better: false },
      { label: "Maintenance + work orders", nestplot: NESTPLOT_MAINTENANCE, competitor: "Included (Facilities module add-on)", nestplot_better: false },
      { label: "Support SLA", nestplot: "Email + in-app, ≤1d response", competitor: "G2: '45+ min hold times, days-to-weeks email response'", nestplot_better: true },
      { label: "API access", nestplot: "First-party REST API (included)", competitor: "Per-module APIs, integration fees", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "RealPage Screening add-on", nestplot_better: true },
    ],
    complaints: [
      "DOJ antitrust settlement Nov 2025: 3-year court-appointed monitor + ban on competitor real-time data use + algorithmic-pricing restrictions",
      "Support: '45+ minutes each call,' 'days or weeks for email replies,' tickets bounce between ~50 acquired modules' departments",
      "Dated UI — 'completely outdated, not user-friendly'; 'New Experience' rewrite slow and error-prone",
      "Nickel-and-diming: 'They charge a fee for everything — even downloading your own data'; ~$100/mo per add-on module",
      "Long onboarding, fragmented navigation, minimal in-app guidance",
    ],
    meta_title: "NestPlot vs RealPage OneSite — modern flat-price alternative for multifamily operators of every size",
    meta_description:
      "OneSite is for Top-50 NMHC operators with six-figure budgets. NestPlot is flat from $59/mo with all modules, API, and Stripe Connect — and no DOJ antitrust monitor.",
  },
  // ====================================================================
  // VEHICLE_STORAGE (marinas, RV/boat)
  // ====================================================================
  {
    slug: "storedge-marina",
    vertical: "vehicle_storage",
    display_name: "storEDGE Marina (Storable Marine)",
    one_liner:
      "Storable's marina arm — same parent company, same 'support degraded after acquisition' complaints flooding the Self-Storage Talk forums.",
    competitor_pricing: {
      headline: "Contact sales",
      transparent: false,
      citation: "https://www.storablemarine.com/marina-management-software/",
      citation_label: "Storable Marine product page (vendor site as of 2026-06)",
    },
    opener:
      "Storable owns storEDGE, SiteLink, Easy Storage Solutions, Molo, and the payment processor underneath all of them. Operators on every product report the same support thread. NestPlot ships flat pricing, bring-your-own Stripe, and a 1-business-day response SLA.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "Contact sales", nestplot_better: true },
      { label: "Parent company", nestplot: "Independent", competitor: "Storable (acquired storEDGE 2018; Molo 2021)", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Storable Payments / Select Merchant Services (mandatory)", nestplot_better: true },
      { label: "Slip + dock plan management", nestplot: NESTPLOT_DOCK_PLAN, competitor: "Included (marina core)", nestplot_better: false },
      { label: "Transient + seasonal billing", nestplot: "Included with prorated logic", competitor: "Included; prorated-billing gaps flagged (Software Advice)", nestplot_better: true },
      { label: "Gate / dock access", nestplot: NESTPLOT_GATE, competitor: "Storable hardware ecosystem", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Notice PDF; no in-product certified mail", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "QuickBooks export", nestplot_better: true },
      { label: "Support SLA", nestplot: "Email + in-app, ≤1d response", competitor: "Self-Storage Talk: 'DAYS to get a response' post-acquisition", nestplot_better: true },
      { label: "Team permissions + audit", nestplot: NESTPLOT_RBAC, competitor: "Coarse user roles", nestplot_better: true },
      { label: "Portfolio dashboard", nestplot: NESTPLOT_PORTFOLIO, competitor: "Per-facility navigation", nestplot_better: true },
    ],
    complaints: [
      "'Since acquisition the technical support has been atrocious. Tickets get lost; DAYS to get a response' — Self-Storage Talk",
      "Rate increases paired with declining service quality post-Storable acquisition (Capterra)",
      "Roll-up consolidation: Storable owns SiteLink, storEDGE, Molo, Select Merchant Services, PAYA",
      "Lacks comprehensive prorated billing (Software Advice)",
      "Reported 7 help-desk staff supporting 4,000+ locations",
    ],
    meta_title: "NestPlot vs storEDGE Marina — operator-owned alternative to Storable Marine",
    meta_description:
      "storEDGE Marina is Storable-owned with the same post-acquisition support pain operators report on self-storage. NestPlot is flat from $59/mo with Stripe Connect.",
  },
  {
    slug: "molo",
    vertical: "vehicle_storage",
    display_name: "Molo (Storable Marine)",
    one_liner:
      "Once the standout indie marina startup — acquired by Storable in 2021, now showing the same payment-lock-in and support-degradation complaints as the rest of the Storable portfolio.",
    competitor_pricing: {
      headline: "Contact sales",
      transparent: false,
      citation: "https://getmolo.com/",
      citation_label: "Molo vendor site as of 2026-06",
    },
    opener:
      "Molo's modern UI is real. So is the Capterra refrain: 'customer pays, Molo collects, you never have a definitive deposit from Molo to you.' NestPlot lets you keep your own Stripe Connect account — your money lands in your bank, not your software vendor's.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "Contact sales", nestplot_better: true },
      { label: "Parent company", nestplot: "Independent", competitor: "Storable (acquired August 2021)", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Molo-mediated; deposit-timing complaints on Capterra", nestplot_better: true },
      { label: "Slip + dock plan management", nestplot: NESTPLOT_DOCK_PLAN, competitor: "Included (modern UI)", nestplot_better: false },
      { label: "Transient + seasonal billing", nestplot: "Included with prorated logic", competitor: "Rigid handling of moves + rate adjustments (Capterra)", nestplot_better: true },
      { label: "Gate / dock access", nestplot: NESTPLOT_GATE, competitor: "Storable hardware ecosystem", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Not surfaced publicly", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "QuickBooks export", nestplot_better: true },
      { label: "Feature disclosure", nestplot: "Public pricing + docs", competitor: "Features gated behind sales calls (Capterra)", nestplot_better: true },
      { label: "Self-serve signup", nestplot: "Sign up + go in 60s", competitor: "Sales-led demo + onboarding", nestplot_better: true },
      { label: "Portfolio dashboard", nestplot: NESTPLOT_PORTFOLIO, competitor: "Per-marina navigation", nestplot_better: true },
    ],
    complaints: [
      "Capterra: 'customer pays, Molo collects, you never have a definitive deposit from Molo to you — they keep your money'",
      "Concern that the processing fee structure 'could put small marinas out of business' (Capterra)",
      "Rigid handling of boat moves, rate adjustments, and booking modifications",
      "Features gated behind sales conversations — operators don't know what's available without asking",
      "Acquired by Storable Aug 2021 — early signs of the same support-degradation pattern that hit storEDGE",
    ],
    meta_title: "NestPlot vs Molo — bring your own Stripe, keep your deposits",
    meta_description:
      "Molo is now Storable Marine. Operators on Capterra report deposit-timing complaints and processing lock-in. NestPlot is flat from $59/mo with operator-owned Stripe Connect.",
  },
  {
    slug: "pier-solution",
    vertical: "vehicle_storage",
    display_name: "PierVantage",
    one_liner:
      "Cloud boatyard / service-and-repair platform with a marina module bolted on — strongest for repair yards, thinner for transient-heavy marinas.",
    competitor_pricing: {
      headline: "Contact sales",
      transparent: false,
      citation: "https://www.piervantage.com/",
      citation_label: "PierVantage vendor site as of 2026-06",
    },
    opener:
      "PierVantage is a repair-yard product first, marina second. NestPlot is built around the operator workflow — slip plan, transient billing, gate access, lien process — without a multi-call quote dance.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "Contact sales", nestplot_better: true },
      { label: "Primary use case", nestplot: "Marina + RV + vehicle storage", competitor: "Boatyard / service & repair (marina secondary)", nestplot_better: true },
      { label: "Slip + dock plan management", nestplot: NESTPLOT_DOCK_PLAN, competitor: "Included (marina module)", nestplot_better: false },
      { label: "Work order / repair tracking", nestplot: "Available via marina ops module", competitor: "Included (core strength)", nestplot_better: false },
      { label: "Transient + seasonal billing", nestplot: "Included with prorated logic", competitor: "Marina-module dependent", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Not publicly disclosed", nestplot_better: true },
      { label: "Gate / dock access", nestplot: NESTPLOT_GATE, competitor: "Not surfaced publicly", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Not surfaced publicly", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "QuickBooks integration", nestplot_better: true },
      { label: "Independent review footprint", nestplot: "Public docs + transparent pricing", competitor: "Thin third-party signal; vendor-curated testimonials", nestplot_better: true },
      { label: "Self-serve signup", nestplot: "Sign up + go in 60s", competitor: "Demo-only path", nestplot_better: true },
    ],
    complaints: [
      "Work-history access 'a bit cumbersome to get to' (G2)",
      "Boatyard-first design — slip and transient flows feel bolted on for pure marina ops",
      "Steep learning curve for non-technical dock staff",
      "No published pricing; no transparent demo path",
      "Small installed base = very thin independent review signal for operators evaluating",
    ],
    meta_title: "NestPlot vs PierVantage — marina-first ops with flat pricing",
    meta_description:
      "PierVantage is built for boatyards first, marinas second. NestPlot is the marina + RV operator alternative: flat from $59/mo, Stripe Connect, transparent pricing.",
  },
  {
    slug: "dockwa",
    vertical: "vehicle_storage",
    display_name: "Dockwa",
    one_liner:
      "Boater-facing transient marketplace plus a modular SaaS back-office — strongest at filling empty transient slips, thinner on full operational depth.",
    competitor_pricing: {
      headline: "Transient $169, Captains & Contracts $180, POS $249, Fuel $199 per month (modular, annual billing)",
      transparent: true,
      citation: "https://marinas.dockwa.com/marina-software-pricing",
      citation_label: "Dockwa marina software pricing",
    },
    opener:
      "Dockwa earned its install base on the boater-facing app. The marina back office is modular — stack three modules and you're past $500/mo. NestPlot ships the full ops layer for flat from $59/mo.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "$169–$249 per module per month (modular)", nestplot_better: true },
      { label: "Three-module stack cost", nestplot: NESTPLOT_PRICE, competitor: "$169 + $180 + $249 = $598/mo", nestplot_better: true },
      { label: "Boater-facing marketplace", nestplot: "Public rental page on nestplot.com", competitor: "Dockwa app (industry-leading reach)", nestplot_better: false },
      { label: "Slip + dock plan management", nestplot: NESTPLOT_DOCK_PLAN, competitor: "Included (Transient module)", nestplot_better: false },
      { label: "Seasonal contracts", nestplot: "Included with prorated logic", competitor: "Captains & Contracts module ($180/mo)", nestplot_better: true },
      { label: "Point of sale / retail", nestplot: "Included", competitor: "POS module ($249/mo)", nestplot_better: true },
      { label: "Fuel + pump-out", nestplot: "Available via vehicle storage ops", competitor: "Fuel module ($199/mo)", nestplot_better: false },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Dockwa-mediated; daily-rate billing bugs reported (Hull Truth)", nestplot_better: true },
      { label: "Gate / dock access", nestplot: NESTPLOT_GATE, competitor: "Not native; partner-dependent", nestplot_better: true },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "Not in scope", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "QuickBooks integration", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
    ],
    complaints: [
      "Hull Truth: 'Most boaters who commented indicated they hate Dockwa'",
      "Daily-rate calculation bug — one user billed 21 single-day rates instead of a prorated month",
      "Slip-type opacity — boaters can't tell what they're booking; ingress/egress not verifiable",
      "Marinas reportedly dropped Dockwa over historical commission tier (~30% take, $1,000 minimum listing)",
      "Outdated listings — marinas that quit Dockwa stay visible, confusing boaters",
    ],
    meta_title: "NestPlot vs Dockwa — full marina ops without per-module pricing",
    meta_description:
      "Dockwa's modules add up: $598/mo for transient + contracts + POS. NestPlot is flat from $59/mo with slip plan, payments, gate, and lien workflow built in.",
  },
  {
    slug: "marina-master",
    vertical: "vehicle_storage",
    display_name: "Marina Master",
    one_liner:
      "Europe-grown enterprise marina platform — feature-rich (RFID, fuel, charter, RV/camping, F&B) but per-user pricing and EU-first integrations.",
    competitor_pricing: {
      headline: "~$100/user/month (Capterra third-party listing); vendor page is contact-sales",
      transparent: false,
      citation: "https://www.capterra.in/software/110609/marina-master",
      citation_label: "Capterra Marina Master listing",
    },
    opener:
      "Marina Master is the right tool if you have RFID gates, fuel docks, a restaurant, and a charter fleet across an Adriatic-sized marina. For a US marina or RV-and-boat operator of any size, NestPlot is flat from $59/mo with US-native Stripe and lien workflow.",
    rows: [
      { label: "Published price", nestplot: NESTPLOT_PRICE, competitor: "~$100/user/mo (third-party); vendor is contact-sales", nestplot_better: true },
      { label: "Pricing model", nestplot: "Flat per facility", competitor: "Per-user — scales painfully with seasonal staff", nestplot_better: true },
      { label: "Target market", nestplot: "US marina + RV + vehicle storage operators", competitor: "EU/Adriatic enterprise multi-revenue marinas", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "EU processors; US-native Stripe / ACH lags", nestplot_better: true },
      { label: "Slip + dock plan management", nestplot: NESTPLOT_DOCK_PLAN, competitor: "Included (deep — RFID/NFC sensors)", nestplot_better: false },
      { label: "Fuel / pump-out integration", nestplot: "Available via vehicle storage ops", competitor: "Included (strong)", nestplot_better: false },
      { label: "F&B / restaurant POS", nestplot: "Not in scope", competitor: "Included", nestplot_better: false },
      { label: "Lien sale workflow", nestplot: NESTPLOT_LIEN, competitor: "EU process; US state coverage gaps", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "EU accounting integrations first", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not US-native", nestplot_better: true },
      { label: "Onboarding speed", nestplot: "Self-serve, ~60s", competitor: "Long — feature surface requires training", nestplot_better: true },
      { label: "Support timezone", nestplot: "Email + in-app, ≤1d response", competitor: "EU timezone — US east-coast afternoon lag", nestplot_better: true },
    ],
    complaints: [
      "Per-user pricing scales painfully for marinas with many seasonal dock staff",
      "EU/Adriatic-centric — US-native payment processors and tax handling lag",
      "Heavy feature surface means longer onboarding for single-site US marinas",
      "Support lives on EU timezone — US afternoon response lag",
      "Vendor pricing is sales-gated; only third-party listings disclose per-user rate",
    ],
    meta_title: "NestPlot vs Marina Master — flat US-native marina + RV alternative",
    meta_description:
      "Marina Master is feature-rich but per-user-priced and EU-first. NestPlot is flat from $59/mo with US-native Stripe, state lien workflow, and self-serve signup.",
  },
  // ====================================================================
  // PARKING
  // ====================================================================
  {
    slug: "parkmobile",
    vertical: "parking",
    display_name: "ParkMobile",
    one_liner:
      "Consumer-facing transient parking app — EasyPark-owned, 50M+ users, infamous for the 2021 breach that exposed 21M users and ended in a $32.8M settlement.",
    competitor_pricing: {
      headline: "$0.20–$0.50 per-transaction driver convenience fee; operator/municipal rates contact-sales",
      transparent: false,
      citation: "https://krebsonsecurity.com/2021/04/parkmobile-breach-exposes-license-plate-data-mobile-numbers-of-21m-users/",
      citation_label: "KrebsOnSecurity · 21M-user ParkMobile breach + $32.8M settlement",
    },
    opener:
      "ParkMobile is a consumer app first — your drivers pay a convenience fee on every session, you give up revenue share, and the 2021 breach exposed 21M users' license plates and phone numbers. NestPlot is flat from $59/mo operator-side with no driver-side stacked fees and no breach history.",
    rows: [
      { label: "Pricing model", nestplot: NESTPLOT_PRICE, competitor: "$0.20–$0.50 per driver transaction + operator rev share", nestplot_better: true },
      { label: "Operator pricing transparency", nestplot: "Published flat from $59/mo", competitor: "Contact sales for operator/municipal rates", nestplot_better: true },
      { label: "Driver-side convenience fee", nestplot: "None", competitor: "$0.30–$0.50/session (varies by city)", nestplot_better: true },
      { label: "Data breach history", nestplot: "None", competitor: "2021 breach: 21M users, $32.8M class settlement", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "ParkMobile processor (mandatory)", nestplot_better: true },
      { label: "Tenant / monthly permit management", nestplot: NESTPLOT_PARKING_PERMIT, competitor: "Consumer-app focus; permit handling thin", nestplot_better: true },
      { label: "Gate / access control", nestplot: NESTPLOT_GATE, competitor: "Plate-recognition / app-based; partner-dependent", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available for operators", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "ParkMobile reporting export", nestplot_better: true },
      { label: "Lien / unpaid collection workflow", nestplot: NESTPLOT_LIEN, competitor: "Citation flow only — no operator lien process", nestplot_better: true },
      { label: "Driver-side support quality", nestplot: "Operator-owned support", competitor: "BBB: double-charges, refused refunds, told to chargeback", nestplot_better: true },
      { label: "SMS-smishing brand risk", nestplot: "None", competitor: "Active smishing wave impersonating 'outstanding fees'", nestplot_better: true },
    ],
    complaints: [
      "2021 breach exposed ~21M users (email, phone, license plate, hashed password, DOB) — $32.8M class-action settlement",
      "BBB: double-charged sessions, $44.95 'ParkMobile for Business' charge after declining sales call",
      "ComplaintsBoard: customer paid 9:25–10:25pm, left 10:17pm, still received citation (app/enforcement sync failure)",
      "Trustpilot 1.1/5: 'riddled with hidden fees,' refunds refused, drivers told to chargeback",
      "Active SMS smishing wave impersonating ParkMobile 'outstanding fees'",
    ],
    meta_title: "NestPlot vs ParkMobile — operator-side flat pricing, no breach history",
    meta_description:
      "ParkMobile charges drivers a per-session fee, takes operator rev share, and had a 21M-user breach in 2021. NestPlot is flat from $59/mo operator-side with Stripe Connect.",
  },
  {
    slug: "paybyphone",
    vertical: "parking",
    display_name: "PayByPhone",
    one_liner:
      "Global mobile-parking incumbent (110M+ downloads) — driver-side per-session fees stacked with SMS-receipt fees, currently being acquired by Lightyear Capital.",
    competitor_pricing: {
      headline: "$0.35 per-transaction driver fee + SMS receipt/reminder fees ($0.15/msg US); operator rates contact-sales",
      transparent: false,
      citation: "https://www.sfmta.com/getting-around/drive-park/parking-meters/pay-park-your-phone",
      citation_label: "SFMTA — PayByPhone $0.35 per-session disclosure",
    },
    opener:
      "PayByPhone makes its money two ways from your drivers: $0.35 per session plus per-SMS fees they can no longer opt out of. NestPlot is flat from $59/mo operator-side — drivers pay you, you keep the spread.",
    rows: [
      { label: "Pricing model", nestplot: NESTPLOT_PRICE, competitor: "$0.35/session + per-SMS fees on drivers", nestplot_better: true },
      { label: "Operator pricing transparency", nestplot: "Published flat from $59/mo", competitor: "Contact sales (one 2018 MA RFP showed $0.11/txn operator side)", nestplot_better: true },
      { label: "SMS receipt opt-out", nestplot: "Operator-controlled notifications", competitor: "Business accounts can no longer opt out (Dec 2025)", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "PayByPhone-mediated", nestplot_better: true },
      { label: "Ownership stability", nestplot: "Independent", competitor: "VW → Corpay/FLEETCOR → Lightyear Capital (Q2 2026)", nestplot_better: true },
      { label: "Tenant / monthly permit management", nestplot: NESTPLOT_PARKING_PERMIT, competitor: "Consumer-app focus", nestplot_better: true },
      { label: "Gate / access control", nestplot: NESTPLOT_GATE, competitor: "Plate-recognition / app; partner-dependent", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available for operators", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "Reporting export only", nestplot_better: true },
      { label: "Lien / unpaid collection workflow", nestplot: NESTPLOT_LIEN, competitor: "Citation flow only", nestplot_better: true },
      { label: "Support channel", nestplot: "Email + in-app, ≤1d response", competitor: "Chatbot-only; no reachable human", nestplot_better: true },
      { label: "Driver-facing complaint volume", nestplot: "Operator-owned support", competitor: "PissedConsumer + Trustpilot: overcharges, denied refunds", nestplot_better: true },
    ],
    complaints: [
      "Driver overcharges vs displayed tariff (e.g. £13.40 charged on £4.20 parking — PissedConsumer)",
      "Unauthorized / duplicate charges weeks after last use",
      "SMS fees stack on top of parking — disclosed only in fine-print fee page",
      "Chatbot-only support; no human reachable; no UK phone number",
      "Refund denials when app fails; drivers ticketed despite paying, told to fight in court",
    ],
    meta_title: "NestPlot vs PayByPhone — operator-side flat pricing without SMS fees",
    meta_description:
      "PayByPhone stacks $0.35/session + SMS fees on drivers. NestPlot is flat from $59/mo operator-side with Stripe Connect — your drivers pay you directly.",
  },
  {
    slug: "flowbird",
    vertical: "parking",
    display_name: "Flowbird",
    one_liner:
      "Enterprise municipal incumbent — kiosk hardware in ~6,000 cities, sold by multi-year RFP, now part of EasyPark Group / Arrive after the Jan 2025 acquisition.",
    competitor_pricing: {
      headline: "Contact sales (Sourcewell / OMNIA cooperative contracts; per-meter SaaS + hardware)",
      transparent: false,
      citation: "https://www.sourcewell-mn.gov/cooperative-purchasing/080321-PRK",
      citation_label: "Sourcewell cooperative contract 080321-PRK",
    },
    opener:
      "Flowbird is the right tool if your city signs five-year cooperative RFPs and wants Pay & Display kiosks on every corner. For a private operator with a lot and an attendant, NestPlot is flat from $59/mo — no kiosk capex, no firmware lockout, no Charleston-style contract reevaluation.",
    rows: [
      { label: "Pricing model", nestplot: NESTPLOT_PRICE, competitor: "Per-meter SaaS + per-kiosk hardware (contact sales)", nestplot_better: true },
      { label: "Sales motion", nestplot: "Self-serve signup, ~60s", competitor: "Multi-year municipal RFP via Sourcewell / OMNIA", nestplot_better: true },
      { label: "Hardware lock-in", nestplot: "Cloud-only, BYO devices", competitor: "Proprietary Strada Pay & Display kiosks", nestplot_better: true },
      { label: "Cold-weather reliability", nestplot: "Software-only — N/A", competitor: "Coin selectors spit coins back (Ephrata PA, firmware bug)", nestplot_better: true },
      { label: "Driver-side convenience fee", nestplot: "None", competitor: "$0.20+ per Strada Pay app transaction (Beverly Hills)", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Flowbird-mediated", nestplot_better: true },
      { label: "Tenant / monthly permit management", nestplot: NESTPLOT_PARKING_PERMIT, competitor: "Municipal permit module", nestplot_better: false },
      { label: "Gate / access control", nestplot: NESTPLOT_GATE, competitor: "Plate-recognition + kiosk", nestplot_better: true },
      { label: "Lien / unpaid collection workflow", nestplot: NESTPLOT_LIEN, competitor: "Citation flow only", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available for private operators", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "Per-contract export", nestplot_better: true },
      { label: "Recent contract churn", nestplot: "No regulatory baggage", competitor: "Charleston SC publicly reevaluating; West Chester PA considering replacement", nestplot_better: true },
    ],
    complaints: [
      "Coin selectors spit coins back in cold weather — Ephrata Borough PA Council publicly called out vendor for not disclosing firmware bug",
      "Strada Pay app declines cards, requires strong WiFi — drivers report 5–10 retries",
      "Enforcement lag: citations issued during paid sessions due to sync failure",
      "City of Charleston SC publicly reevaluating Flowbird contract (WCBD)",
      "UX inaccessible for elderly drivers; downtown merchants report customers driven away",
    ],
    meta_title: "NestPlot vs Flowbird — flat-price alternative for private parking operators",
    meta_description:
      "Flowbird sells to cities via multi-year RFPs with kiosk hardware lock-in. NestPlot is flat from $59/mo for private operators — no kiosks, no firmware bugs, no driver fees.",
  },
  {
    slug: "passport-parking",
    vertical: "parking",
    display_name: "Passport Parking",
    one_liner:
      "Sixth Street-backed mobility-payments platform, white-labeled across 1,000+ cities — but Apple Pay support missing in 2026 and the support runaround is a recurring complaint.",
    competitor_pricing: {
      headline: "Contact sales (driver fees $0.15–$0.25/session per city)",
      transparent: false,
      citation: "https://www.srcity.org/DocumentCenter/View/23354/Santa-Rosa-Passport-FAQ",
      citation_label: "Santa Rosa CA — Passport $0.15/session disclosure",
    },
    opener:
      "Passport raised $200M+ across Bain (Series C 2017) and Sixth Street (2021) and uses it to white-label parking apps for 1,000 cities. The operator side is sales-gated. NestPlot is flat from $59/mo with the integrations and audit trail published.",
    rows: [
      { label: "Pricing model", nestplot: NESTPLOT_PRICE, competitor: "$0.15–$0.25/session driver fee + operator rev share", nestplot_better: true },
      { label: "Operator pricing transparency", nestplot: "Published flat from $59/mo", competitor: "Contact sales for operator/municipal rates", nestplot_better: true },
      { label: "Funding posture", nestplot: "Independent, operator-aligned", competitor: "$200M+ raised; VC-driven roadmap", nestplot_better: true },
      { label: "Apple Pay support", nestplot: "Stripe-native, all wallets", competitor: "Not supported in 2026 — manual card re-entry", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Passport-mediated", nestplot_better: true },
      { label: "Tenant / monthly permit management", nestplot: NESTPLOT_PARKING_PERMIT, competitor: "Municipal permit module", nestplot_better: false },
      { label: "Gate / access control", nestplot: NESTPLOT_GATE, competitor: "Plate-recognition partner integrations", nestplot_better: true },
      { label: "Lien / unpaid collection workflow", nestplot: NESTPLOT_LIEN, competitor: "Citation flow only", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available for private operators", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "Per-contract export", nestplot_better: true },
      { label: "Support workflow", nestplot: "Operator-owned, ≤1d response", competitor: "Driver-Passport-city runaround loops (JustUseApp)", nestplot_better: true },
      { label: "Zone-finding UX", nestplot: "Public rental page — operator-controlled", competitor: "GPS doesn't auto-detect zone; drivers hunt markers", nestplot_better: true },
    ],
    complaints: [
      "Payment confirmations fail silently — drivers get tickets thinking they paid",
      "Unexplained charges Passport CS 'cannot locate the source of'",
      "Support runaround — Passport tells driver to call city; city says call Passport; weeks of loops",
      "No Apple Pay support in 2026; manual card re-entry",
      "Zone-finding UX broken; GPS doesn't auto-detect zone (JustUseApp)",
    ],
    meta_title: "NestPlot vs Passport Parking — operator-side flat pricing",
    meta_description:
      "Passport raised $200M+ to white-label parking apps for cities. NestPlot is flat from $59/mo for private operators — Apple Pay, Stripe Connect, audit trail included.",
  },
  {
    slug: "parkloyalty",
    vertical: "parking",
    display_name: "Park Loyalty",
    one_liner:
      "Milwaukee enforcement-and-citations startup (founded 2023, ~14 employees, Series A Nov 2025) — narrowly built for municipal citation processing, not full operator ops.",
    competitor_pricing: {
      headline: "Contact sales (no published pricing)",
      transparent: false,
      citation: "https://parkloyalty.com/",
      citation_label: "Park Loyalty vendor site as of 2026-06",
    },
    opener:
      "Park Loyalty is built for municipal citation processing — Enforce Pro, Process Pro, Permit Pro, Rewards Pro. It is not a private operator's PMS. NestPlot is flat from $59/mo with the operator workflow (gate, lease, payments, lien) built in.",
    rows: [
      { label: "Pricing model", nestplot: NESTPLOT_PRICE, competitor: "Contact sales — no public tiers or per-citation rate", nestplot_better: true },
      { label: "Target customer", nestplot: "Private parking operators of every size", competitor: "Municipalities, universities, hospitals, transit authorities", nestplot_better: true },
      { label: "Product scope", nestplot: "Full operator PMS", competitor: "Enforcement + citations + permits + rewards (no PARCS depth)", nestplot_better: true },
      { label: "Company maturity", nestplot: "Independent, multi-product", competitor: "Founded 2023; ~14 employees (PitchBook); Series A Nov 2025", nestplot_better: true },
      { label: "Independent review footprint", nestplot: "Public docs + transparent pricing", competitor: "No G2 / Capterra / BBB / Reddit signal", nestplot_better: true },
      { label: "Payment processing", nestplot: NESTPLOT_STRIPE, competitor: "Not publicly disclosed", nestplot_better: true },
      { label: "Tenant / monthly permit management", nestplot: NESTPLOT_PARKING_PERMIT, competitor: "Permit Pro module", nestplot_better: false },
      { label: "Gate / access control", nestplot: NESTPLOT_GATE, competitor: "No PARCS / gate hardware integration", nestplot_better: true },
      { label: "Dynamic pricing", nestplot: "Auto-apply with operator-set caps + audit trail", competitor: "Not in product scope", nestplot_better: true },
      { label: "Lien / unpaid collection workflow", nestplot: NESTPLOT_LIEN, competitor: "Citation flow (Process Pro) — municipal focus", nestplot_better: true },
      { label: "Identity verification", nestplot: NESTPLOT_IDENTITY, competitor: "Not available", nestplot_better: true },
      { label: "Multi-entity accounting", nestplot: NESTPLOT_ACCOUNTING, competitor: "Not publicly disclosed", nestplot_better: true },
    ],
    complaints: [
      "Thin third-party review footprint — no G2, Capterra, BBB, Reddit, or operator-forum signal",
      "Tiny company (~14 employees) claiming 4M citations/year — support bandwidth question for private operators",
      "Brand-new — founded 2023, ~3 years production history; Series A just closed Nov 2025",
      "Wrong product shape for commercial garage operators — no PARCS gate hardware, no dynamic pricing depth",
      "Pricing opacity — no public tiers, no per-citation transparency",
    ],
    meta_title: "NestPlot vs Park Loyalty — operator PMS, not municipal citation processing",
    meta_description:
      "Park Loyalty is a 2023 Milwaukee startup built for municipal citations — not a private operator's PMS. NestPlot is flat from $59/mo with gate, lease, and Stripe Connect.",
  },
];

export function findCompetitor(slug: string): Competitor | undefined {
  return COMPETITORS.find((c) => c.slug === slug);
}
